import React from 'react';
import type {
  AhaSlateEditor,
  EditorPlugin,
  LayoutComponent,
  SetMetadataOperation,
} from '../../types/aha-editor';
import { observable, raw, ApplicationView } from '@aha-app/mvc';
import TableOfContents from './components/TableOfContents';
import { Editor, Element, Node } from 'slate';
import BlockUtils from '../Blocks/BlockUtils';
import type { HeadingElement, HeadingType } from './types';

const headingTypes = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

export const getHeadings = (editor: Editor): HeadingElement[] => {
  const headings: HeadingElement[] = [];
  const headingStack: HeadingElement[] = [];

  const headingElements = Editor.nodes(editor, {
    at: [],
    match: (n: Node): n is Element & { type: HeadingType } => {
      return (
        Element.isElement(n) &&
        headingTypes.has(n.type) &&
        Boolean(Node.string(n))
      );
    },
  });

  for (const [node] of headingElements) {
    while (headingStack.length > 0 && node.type <= headingStack.at(-1)!.type) {
      headingStack.pop();
    }

    const lastHeading = headingStack.at(-1);

    const nodeText = Node.string(node);
    const headingElement: HeadingElement = {
      type: node.type,
      id: BlockUtils.idFromText(nodeText),
      text: nodeText,
      children: [],
    };

    if (lastHeading) {
      lastHeading.children.push(headingElement);
    } else {
      headings.push(headingElement);
    }

    headingStack.push(headingElement);
  }

  return headings;
};

interface TableOfContentsState {
  enabled: boolean;
  headings: HeadingElement[];
}

const TableOfContentsPlugin = (): EditorPlugin => {
  const state = observable<TableOfContentsState>({
    enabled: false,
    headings: [],
  });

  let editor: AhaSlateEditor;
  const Component: LayoutComponent = (...args) => {
    if (!state.enabled) return null;
    if (!editor) return null;

    return <TableOfContents headings={raw(state.headings)} editor={editor} />;
  };

  return {
    name: 'Table of contents plugin',
    id: 'TableOfContentsPlugin',
    handlers: {
      onInit({ editor: _editor }) {
        editor = _editor;
        state.enabled = editor.metadata?.tableOfContentsEnabled ?? false;
      },

      onMount({ editor }) {
        if (editor.metadata?.tableOfContentsEnabled) {
          requestIdleCallback(() => {
            state.headings = getHeadings(editor);
          });
        }
      },

      onChange({ editor }) {
        if (state.enabled) {
          requestIdleCallback(() => {
            state.headings = getHeadings(editor);
          });
        } else {
          state.headings = [];
        }
      },
    },
    operationHandler: {
      operation: 'set_metadata',
      run(editor: Editor, op: SetMetadataOperation) {
        const {
          newProperties: { key, value },
        } = op;

        if (key === 'tableOfContentsEnabled') {
          state.enabled = value;
        }

        return false;
      },
    },
    layoutComponent: {
      location: 'above_content',
      Component: ApplicationView(Component),
    },
  };
};

export default TableOfContentsPlugin;
